import { createSelector } from '@ngrx/store';
import { CalculatedPackage, MotorsMetadata } from 'libs/datalayer/src/lib/apimodels/draft/metadata';
import { CategoryEnum } from 'libs/datalayer/src/lib/enums/category';
import { MotorsDraftPartialState, DRAFT_MOTORS_FEATURE_KEY } from '@classifieds-sell/state';
import {
    RouterPartialState,
    RouterState,
    ROUTER_FEATURE_KEY
} from '@classifieds-sell/state';
import { getListingPrice, getPackagePrice } from '../../helpers/price.helper';

export interface FeeCalculationState
    extends MotorsDraftPartialState,
    RouterPartialState { }

const draft = (state: FeeCalculationState) => state[DRAFT_MOTORS_FEATURE_KEY];
const router = (state: FeeCalculationState) => state[ROUTER_FEATURE_KEY];

const draftStateSelector = createSelector(draft, (d) => d);
const routerStateSelector = createSelector(router, (r) => r);

function isEditOrRelist(routerState: RouterState) {
    return routerState.data.editing || routerState.data.relisting;
}


function feeDeterminer(
    category: CategoryEnum | undefined,
    draftData: any,
    metadata: MotorsMetadata,
    effectiveFeeDeterminer: number | undefined
): number {
    const listingPrice = getListingPrice(category, draftData);
    if (!effectiveFeeDeterminer) {
        return listingPrice;
    }
    return effectiveFeeDeterminer > listingPrice
        ? effectiveFeeDeterminer
        : listingPrice;
}

const listingFeeTable = createSelector(draftStateSelector, (draftState) => {
    return draftState.metadata.fees.filter(f => f.sellingType === draftState.draftData.listingDetails.sellingType);
});

const currentListingPackageInformation = createSelector(
    draftStateSelector,
    (draftState) => {
        return draftState.metadata.packageInformation;
    }
);

const disabledPackagesForSelection = createSelector(
    currentListingPackageInformation,
    listingFeeTable,
    routerStateSelector,
    (packageInformation, feeTable, routerState) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        if (!isEditOrRelistPage) {
            return [];
        }
        return feeTable
            .filter((fee) => fee.id < packageInformation.listingPackageId)
            .map((fee) => fee.id);
    }
);


const listingFeeDeterminer = createSelector(
    draftStateSelector,
    routerStateSelector,
    (draftState, routerState) => {
        const draftData = draftState.draftData;
        const effectiveFeeDeterminer =
            draftState.metadata.packageInformation
                ?.effectiveFeeDeterminer;
        const categoryId = routerState.data.categoryId;
        return feeDeterminer(
            categoryId,
            draftData,
            draftState.metadata,
            effectiveFeeDeterminer
        );
    }
);

// start exported members

/**
 * selector used to render the card on property promote page for each card
 */
export const packageFees = createSelector(
    listingFeeDeterminer,
    currentListingPackageInformation,
    listingFeeTable,
    disabledPackagesForSelection,
    routerStateSelector,
    (currentFeeDeterminer, info, feeTable, disabledPackages, routerState) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        return feeTable.map((fee) => {
            const packagePrice = getPackagePrice(
                fee.prices,
                currentFeeDeterminer
            );
            function isDisabledPackage() {
                return disabledPackages.some((p) => p === fee.id);
            }
            function priceForEditOrRelist() {
                if (isDisabledPackage()) {
                    return null; // null will not render price
                }
                const paidAmount = getPackagePrice(
                    feeTable.find((f) => f.id === info.listingPackageId).prices,
                    info.effectiveFeeDeterminer
                );
                return packagePrice - paidAmount;
            }
            return {
                ...fee,
                packagePrice: !isEditOrRelistPage
                    ? packagePrice
                    : priceForEditOrRelist(),
                isDisabled: isDisabledPackage()
            } as CalculatedPackage;
        });
    }
);

/**
 * selector used in property information page to show the popup message
 */
export const isIncreasePricingTier = createSelector(
    listingFeeDeterminer,
    currentListingPackageInformation,
    routerStateSelector,
    packageFees,
    (currentFeeDeterminer, info, routerState, feeTable) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        return (
            isEditOrRelistPage &&
            currentFeeDeterminer > info.effectiveFeeDeterminer
            && !feeTable.some(p => p.packagePrice === 0)
        );
    }
);


/**
 * selector used in property summary page to show the total price
 */
export const totalFee = createSelector(
    draftStateSelector,
    packageFees,
    (draftState, calculatedFees) => {
        const intendedPackageId =
            draftState.draftData.listingDetails.categoryPackageId;

        const sellingType =
            draftState.draftData.listingDetails.sellingType;

        if (!intendedPackageId) {
            return 0;
        }
        const categoryFees = calculatedFees.find(
            (fee) => fee.id === intendedPackageId
              && fee.sellingType === sellingType
        );
        const listingFees = categoryFees.packagePrice;
        return listingFees;
    }
);
